import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import { useFloatingControlGuard } from "@/hooks/useFloatingControlGuard";

interface StickyEstimateButtonProps {
  /** Optional explicit target id; otherwise the button finds the page's form automatically. */
  formId?: string;
  /** Pixels scrolled before the button appears. */
  showAfterPx?: number;
}

/** Ordered list of anchors used by the various page templates. */
const FORM_TARGETS = [
  "#estimate-form-top",
  "#quote-desktop",
  "#quote-mobile",
  "#service-form",
  "#quote-form",
  "#contact-form",
  "#quote",
];

const findFormTarget = (formId?: string): HTMLElement | null => {
  if (formId) {
    const explicit = document.getElementById(formId);
    if (explicit) return explicit;
  }
  for (const sel of FORM_TARGETS) {
    const el = document.querySelector<HTMLElement>(sel);
    if (el && el.offsetParent !== null) return el;
  }
  // Last resort: any real lead form on the page
  const forms = Array.from(document.querySelectorAll<HTMLFormElement>("form"));
  const lead = forms.find(
    (f) => f.querySelector('input[name="name"]') && f.offsetParent !== null
  );
  return lead ?? null;
};

const StickyEstimateButton = ({
  formId,
  showAfterPx = 500,
}: StickyEstimateButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const { ref, obstructed } = useFloatingControlGuard();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(false);

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const scrolled = window.scrollY;
        if (scrolled < showAfterPx) {
          setIsVisible(false);
          return;
        }
        // Hide while a form is actually on screen
        const target = findFormTarget(formId);
        if (target) {
          const rect = target.getBoundingClientRect();
          const onScreen = rect.top < window.innerHeight - 80 && rect.bottom > 120;
          setIsVisible(!onScreen);
          return;
        }
        setIsVisible(true);
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [formId, showAfterPx, location.pathname]);

  const goToForm = useCallback(() => {
    const target = findFormTarget(formId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      const field =
        target.querySelector<HTMLElement>('input[name="name"]') ??
        target.querySelector<HTMLElement>("input, textarea, select") ??
        document.querySelector<HTMLElement>('form input[name="name"]');
      if (field) {
        window.setTimeout(() => field.focus({ preventScroll: true }), 650);
      }
      return;
    }
    // No form on this page — send them to the contact page
    navigate("/contact");
    window.setTimeout(() => {
      const el = findFormTarget();
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
  }, [formId, navigate]);

  return (
    <div ref={ref} data-floating-control aria-hidden={!isVisible || obstructed} className={`fixed bottom-24 right-4 z-40 hidden md:block ${!isVisible || obstructed ? "invisible pointer-events-none" : "visible"}`}>
      <Button
        onClick={goToForm}
        aria-label="Request service or assessment"
        className="bg-accent text-accent-foreground hover:bg-accent/90 ring-1 ring-background/40 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.7)] hover:shadow-[0_14px_36px_-8px_rgba(0,0,0,0.8)] transition-all duration-300 rounded-full px-6 py-3 text-sm font-semibold"
        size="default"
      >
        <FileText className="w-4 h-4 mr-2" />
        Request Service or Assessment
      </Button>
    </div>
  );
};

export default StickyEstimateButton;
